import { Link } from "react-router-dom"
import { ArrowLeft, Calendar, User, Clock, Tag } from "lucide-react"

import { APP_NAME } from "@/lib/appConfig"
import { Button } from "@/components/ui/button"
import type { BlogPostMetadata } from "./content"

interface BlogPostLayoutProps {
    metadata: BlogPostMetadata
    children: React.ReactNode
}

export default function BlogPostLayout({ metadata, children }: BlogPostLayoutProps) {
    return (
        <div className="w-full bg-background flex flex-col items-center">
            <main className="w-full max-w-[800px] mx-auto px-4 py-8 md:py-12 flex-1">
                {/* Back to Blog Link */}
                <div className="mb-8">
                    <Link to="/blog" className="inline-flex items-center gap-2 text-muted-foreground hover:text-foreground transition-colors group">
                        <ArrowLeft className="h-4 w-4 group-hover:-translate-x-1 transition-transform" />
                        <span className="font-medium text-sm">Back to Blog</span>
                    </Link>
                </div>

                {/* Article Header */}
                <header className="mb-12 text-center">
                    <div className="flex flex-wrap items-center justify-center gap-2 mb-6">
                        {metadata.tags.map(tag => (
                            <span key={tag} className="px-3 py-1 bg-primary/10 text-primary text-xs font-bold uppercase tracking-wider rounded-full">
                                {tag}
                            </span>
                        ))}
                    </div>

                    <h1 className="text-4xl md:text-5xl lg:text-6xl font-extrabold tracking-tight text-foreground mb-6 leading-[1.1]">
                        {metadata.title}
                    </h1>

                    <div className="flex flex-wrap items-center justify-center gap-x-6 gap-y-3 text-sm text-muted-foreground font-medium">
                        <div className="flex items-center gap-2">
                            <User className="h-4 w-4" />
                            {metadata.author}
                        </div>
                        <div className="flex items-center gap-2">
                            <Calendar className="h-4 w-4" />
                            {new Date(metadata.date).toLocaleDateString(undefined, { year: 'numeric', month: 'long', day: 'numeric' })}
                        </div>
                        <div className="flex items-center gap-2">
                            <Clock className="h-4 w-4" />
                            {metadata.readTime}
                        </div>
                    </div>
                </header>

                {/* Hero Image */}
                {metadata.coverImage && (
                    <div className="w-full aspect-video rounded-3xl overflow-hidden mb-16 shadow-2xl relative group">
                        <div className="absolute inset-0 bg-gradient-to-t from-black/20 to-transparent z-10" />
                        <img
                            src={metadata.coverImage}
                            alt={metadata.title}
                            className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                        />
                    </div>
                )}

                {/* Article Content (Prose) */}
                <article className="prose prose-lg dark:prose-invert max-w-none prose-headings:font-bold prose-headings:tracking-tight prose-a:text-primary prose-a:no-underline hover:prose-a:underline prose-img:rounded-3xl prose-img:shadow-lg prose-p:leading-relaxed text-foreground/90">
                    {children}
                </article>

                <hr className="my-16 border-border" />

                {/* SEO CTA Banner */}
                <div className="bg-gradient-to-br from-primary to-primary/80 text-primary-foreground p-8 md:p-12 rounded-3xl shadow-glow text-center relative overflow-hidden">
                    <div className="absolute inset-0 bg-[url('/noise.png')] opacity-20 mix-blend-overlay" />
                    <div className="relative z-10">
                        <h2 className="text-3xl md:text-4xl font-extrabold mb-4 tracking-tight">Stop Managing Properties the Hard Way.</h2>
                        <p className="text-lg text-primary-foreground/90 mb-8 max-w-xl mx-auto font-medium">
                            Join thousands of modern landlords using {APP_NAME} to automate rent collection, track maintenance, and maximize ROI.
                        </p>
                        <Button size="lg" variant="secondary" className="h-14 px-8 rounded-full font-bold text-lg shadow-xl hover:scale-105 transition-transform" asChild>
                            <Link to="/register">Create Your Free Account</Link>
                        </Button>
                        <p className="text-sm font-medium text-primary-foreground/70 justify-center flex items-center gap-1 mt-4">
                            No credit card required. Free forever tier available.
                        </p>
                    </div>
                </div>
            </main>
        </div>
    )
}
